
import React, { useState, useEffect } from 'react';
import { SymbolData } from '../types';
import { ArrowLeft, BookOpen, Brain, Image as ImageIcon, ArrowRight, Play, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { DEFAULT_READER_DISPLAY_SETTINGS, ReaderDisplaySettings, ReaderSettingsMenu } from './ReaderDisplaySettings';

interface DetailViewProps {
  data: SymbolData;
  onBack: () => void;
  onNextSymbol?: () => void;
  onPrevSymbol?: () => void;
  displaySettings?: ReaderDisplaySettings;
  onDisplaySettingsChange?: (next: Partial<ReaderDisplaySettings>) => void;
  onDisplaySettingsReset?: () => void;
}

const DetailView: React.FC<DetailViewProps> = ({
  data,
  onBack,
  onNextSymbol,
  onPrevSymbol,
  displaySettings = DEFAULT_READER_DISPLAY_SETTINGS,
  onDisplaySettingsChange,
  onDisplaySettingsReset,
}) => {
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const labelFontSize = Math.max(14, displaySettings.fontSize - 1);
  const bodyTextStyle: React.CSSProperties = {
    fontSize: `${displaySettings.fontSize}px`,
    lineHeight: displaySettings.lineHeight,
  };
  const bodyTextCompactStyle: React.CSSProperties = {
    fontSize: `${Math.max(13, displaySettings.fontSize - 1)}px`,
    lineHeight: displaySettings.lineHeight,
  };
  const paragraphGap = Math.max(4, displaySettings.paragraphSpacing);

  // Reset slide to 0 when data changes (i.e. user navigated to a new symbol)
  useEffect(() => {
    setCurrentSlide(0);
  }, [data.id]);

  // Helper function to render content that might contain "正：" and "负：" lines
  const renderContent = (content: string) => {
    // Regex to split by the specific delimiters, keeping the delimiter
    const parts = content.split(/(?=正：)|(?=负：)/g).filter(p => p.trim() !== '');
    
    // If no special markers found, return simple text
    if (!content.includes('正：') && !content.includes('负：')) {
        return <span className="text-inherit" style={bodyTextStyle}>{content}</span>;
    }

    return (
        <div className="flex flex-col gap-2 w-full mt-1">
            {parts.map((part, idx) => {
                const isPositive = part.trim().startsWith('正：');
                const isNegative = part.trim().startsWith('负：');
                const cleanText = part.replace(/^正：|^负：/, '').trim();

                if (isPositive) {
                    return (
                        <div key={idx} className="flex items-start gap-2 bg-emerald-50 p-2 rounded-lg border border-emerald-100">
                             <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded bg-emerald-200 text-emerald-800 text-sm font-bold">正</span>
                             <span className="text-emerald-900" style={bodyTextCompactStyle}>{cleanText}</span>
                        </div>
                    )
                }
                if (isNegative) {
                    return (
                        <div key={idx} className="flex items-start gap-2 bg-red-50 p-2 rounded-lg border border-red-100">
                             <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded bg-red-200 text-red-800 text-sm font-bold">负</span>
                             <span className="text-red-900" style={bodyTextCompactStyle}>{cleanText}</span>
                        </div>
                    )
                }
                // Fallback for parts without prefix
                return <span key={idx} className="text-inherit" style={bodyTextStyle}>{part}</span>;
            })}
        </div>
    );
  };

  const hasStory = !!data.story;
  const hasTags = data.tags && data.tags.length > 0;

  // --- Presentation Logic ---
  const totalSlides = 1 + data.coreOrigins.length + (hasStory ? 1 : 0); // 0: Title, 1..N: Origins, N+1: Story

  const enterPresentation = () => {
    setCurrentSlide(0);
    setIsPresentationMode(true);
  };

  const exitPresentation = () => {
    setIsPresentationMode(false);
    setIsImageZoomed(false);
  };

  const nextSlide = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(c => c + 1);
    } else if (onNextSymbol) {
      // If at the end of slides, go to next symbol
      onNextSymbol();
    }
  };

  const prevSlide = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentSlide > 0) {
      setCurrentSlide(c => c - 1);
    } else if (onPrevSymbol) {
      // If at the start, go to previous symbol
      onPrevSymbol();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isPresentationMode) return;
      if (isImageZoomed) {
        if (e.key === 'Escape') setIsImageZoomed(false);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        exitPresentation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, currentSlide, totalSlides, isImageZoomed, onNextSymbol, onPrevSymbol]);

  // Render Slide Content
  const renderSlideContent = () => {
    // 1. Title Slide (Cover)
    if (currentSlide === 0) {
      // Use tags for the visual flow, skipping the first one if it's just the character itself to avoid redundancy
      const displayTags = data.tags.length > 1 && data.tags[0] === data.char ? data.tags.slice(1) : data.tags;

      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center animate-fade-in-up font-serif relative z-10">
           
           {/* Decorative Symbol Container */}
           <div className="w-40 h-40 md:w-56 md:h-56 bg-slate-900 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-amber-500 mb-8 transform hover:scale-105 transition-transform duration-500">
              <span className="text-8xl md:text-9xl text-amber-50">{data.char}</span>
           </div>
           
           <h2 className="text-4xl md:text-6xl font-bold text-slate-800 mb-4 tracking-wider">{data.pinyin}</h2>
           <p className="text-xl md:text-2xl text-slate-500 mb-16 font-sans font-light">{data.summary}</p>
           
           {/* Core Keyword Flow */}
           <div className="w-full max-w-5xl">
              <div className="text-sm font-bold text-slate-400 uppercase tracking-[0.2em] mb-6">核心象意概览</div>
              <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6">
                 {displayTags.map((tag, idx) => (
                    <React.Fragment key={idx}>
                        <div className="bg-amber-100 border-2 border-amber-200 text-amber-900 px-6 py-3 md:px-8 md:py-4 rounded-xl shadow-sm text-lg md:text-2xl font-bold relative group hover:-translate-y-1 transition-transform">
                           {tag}
                           {/* Little decorative corner */}
                           <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-amber-400 rounded-tr-sm"></div>
                           <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-amber-400 rounded-bl-sm"></div>
                        </div>
                        {idx < displayTags.length - 1 && (
                           <ArrowRight className="w-6 h-6 md:w-8 md:h-8 text-slate-300" />
                        )}
                    </React.Fragment>
                 ))}
              </div>
           </div>
        </div>
      );
    }

    // 2. Origin Slides
    if (currentSlide > 0 && currentSlide <= data.coreOrigins.length) {
      const origin = data.coreOrigins[currentSlide - 1];
      return (
        <div className="flex flex-col h-full p-6 md:p-12 md:px-20 animate-fade-in font-serif relative z-10 max-w-6xl mx-auto">
           {/* Header */}
           <div className="mb-8 border-b-2 border-slate-200 pb-4">
               <div className="flex items-center gap-3 mb-2 opacity-60 font-sans">
                  <span className="text-xs font-bold bg-slate-800 text-amber-50 px-2 py-1 rounded tracking-wider">原点 {currentSlide} / {data.coreOrigins.length}</span>
               </div>
           <h2 className="text-2xl md:text-5xl font-bold text-slate-900 leading-tight mb-3 md:mb-4">{origin.title}</h2>
           {origin.logic && (
                 <p className="text-sm md:text-xl text-slate-500 italic font-sans leading-relaxed pl-1 border-l-4 border-amber-400">
                   {origin.logic}
                 </p>
               )}
           </div>

           {/* Content List */}
           <div className="flex-1 overflow-y-auto pr-4 space-y-6">
              {origin.subItems.map((sub, idx) => (
                 <div key={idx} className="flex flex-col md:flex-row items-stretch gap-0 md:gap-6 group">
                    {/* Left: Label Box */}
                    <div className="w-full md:w-32 shrink-0 flex items-center justify-center bg-indigo-50 border-2 border-indigo-100 text-indigo-800 p-3 md:p-0 rounded-t-xl md:rounded-xl shadow-sm transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-200">
                        <span className="text-sm md:text-base font-bold tracking-widest">{sub.label}</span>
                    </div>
                    
                    {/* Right: Content Box */}
                    <div className="flex-1 bg-white border border-slate-200 p-3.5 md:p-5 rounded-b-xl md:rounded-xl shadow-sm group-hover:shadow-md transition-all font-sans">
                        <div className="text-sm md:text-base text-slate-700 leading-7 md:leading-8">
                           {renderContent(sub.content)}
                        </div>
                    </div>
                 </div>
              ))}
           </div>
        </div>
      );
    }

    // 3. Story Slide
    if (currentSlide === totalSlides - 1 && hasStory) {
      return (
        <div className="flex flex-col h-full p-6 md:p-12 animate-fade-in font-serif relative z-10">
           <div className="flex items-center justify-center gap-3 mb-4 md:mb-8 opacity-50">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-bold uppercase tracking-widest font-sans">场景故事速记</span>
           </div>

           <div className="flex-1 flex flex-col items-center justify-center gap-10 max-w-5xl mx-auto w-full">
              {/* Image */}
              {data.imageUrl && (
                 <div className="relative w-full max-w-2xl group cursor-zoom-in" onClick={(e) => { e.stopPropagation(); setIsImageZoomed(true); }}>
                    <div className="rounded-2xl overflow-hidden shadow-2xl border-8 border-white bg-white transform rotate-1 group-hover:rotate-0 transition-transform duration-500">
                       <img src={data.imageUrl} alt="Story" className="w-full h-64 md:h-96 object-cover" />
                       {/* Zoom Hint */}
                       <div className="absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <ZoomIn className="w-5 h-5" />
                       </div>
                    </div>
                    {/* Shadow effect */}
                    <div className="absolute inset-0 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] -z-10 transform rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                 </div>
              )}

              {/* Text */}
              <div className="w-full max-w-4xl bg-white/80 backdrop-blur-sm p-8 rounded-2xl border border-amber-100 shadow-lg relative">
                 <div className="absolute -top-3 -left-3 text-6xl text-amber-200 font-serif leading-none">“</div>
                 <p className="text-lg md:text-3xl text-slate-800 leading-relaxed text-center relative z-10">
                    {data.story}
                 </p>
                 <div className="absolute -bottom-10 -right-3 text-6xl text-amber-200 font-serif leading-none rotate-180">“</div>
              </div>
           </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50 text-slate-800 animate-fade-in overflow-hidden relative">
      
      {/* PRESENTATION OVERLAY */}
      {isPresentationMode && (
        <div className="fixed inset-0 z-50 bg-[#fdfbf7] flex flex-col text-slate-800">
           {/* Ancient Texture Overlay */}
           <div className="absolute inset-0 pointer-events-none opacity-50 mix-blend-multiply" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.05\'/%3E%3C/svg%3E")' }}></div>
           
           {/* Decorative Border Frame */}
           <div className="absolute inset-4 border-2 border-stone-200 rounded-3xl pointer-events-none z-0"></div>
           <div className="absolute inset-5 border border-stone-100 rounded-2xl pointer-events-none z-0"></div>

           {/* Image Zoom Modal */}
           {isImageZoomed && data.imageUrl && (
             <div 
                className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out animate-fade-in"
                onClick={() => setIsImageZoomed(false)}
             >
                <img 
                  src={data.imageUrl} 
                  alt="Zoomed" 
                  className="max-w-full max-h-full rounded-lg shadow-2xl scale-100 transition-transform"
                />
             </div>
           )}

           {/* Controls Header */}
           <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-20">
              <div className="text-sm font-bold text-slate-400 uppercase tracking-widest font-sans flex items-center gap-2">
                 <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                 演示模式 · {currentSlide + 1}/{totalSlides}
              </div>
              <button onClick={exitPresentation} className="p-3 bg-white hover:bg-slate-100 border border-slate-200 rounded-full text-slate-600 transition-all hover:rotate-90 shadow-sm">
                 <X className="w-6 h-6" />
              </button>
           </div>

           {/* Slide Content Area */}
           <div className="flex-1 relative overflow-hidden">
              {/* Click zones for navigation */}
              <div className="absolute inset-y-0 left-0 w-1/6 z-10 cursor-w-resize group flex items-center justify-start pl-4" onClick={prevSlide} title="上一页">
                 <div className="p-2 bg-black/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronLeft className="w-8 h-8 text-slate-400" />
                 </div>
              </div>
              <div className="absolute inset-y-0 right-0 w-1/6 z-10 cursor-e-resize group flex items-center justify-end pr-4" onClick={nextSlide} title="下一页">
                 <div className="p-2 bg-black/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRight className="w-8 h-8 text-slate-400" />
                 </div>
              </div>
              
              <div className="h-full w-full relative z-0">
                 {renderSlideContent()}
              </div>
           </div>

           {/* Bottom Navigation Bar */}
           <div className="p-6 pb-8 flex justify-center items-center gap-10 relative z-20">
              <button 
                onClick={prevSlide}
                disabled={currentSlide === 0 && !onPrevSymbol}
                className="p-4 rounded-full hover:bg-stone-100 disabled:opacity-20 transition-colors text-stone-600"
              >
                 <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="flex gap-3">
                 {Array.from({ length: totalSlides }).map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`h-1.5 rounded-full transition-all duration-500 ${idx === currentSlide ? 'bg-amber-500 w-8' : 'bg-stone-300 w-2'}`}
                    ></div>
                 ))}
              </div>

              <button 
                onClick={nextSlide}
                disabled={currentSlide === totalSlides - 1 && !onNextSymbol}
                className="p-4 rounded-full hover:bg-stone-100 disabled:opacity-20 transition-colors text-stone-600"
                title={currentSlide === totalSlides - 1 && onNextSymbol ? "跳转下一个符号" : "下一页"}
              >
                 <ChevronRight className="w-6 h-6" />
              </button>
           </div>
        </div>
      )}

      {/* 1. Navigation Bar (Slim) */}
      <div className="shrink-0 bg-slate-900 text-amber-50 shadow-md z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-slate-700 rounded-full transition-colors flex items-center space-x-2 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium hidden sm:inline opacity-80 group-hover:opacity-100">返回列表</span>
          </button>
          
          <div className="flex items-center gap-4">
             <div className="text-sm font-medium opacity-60 uppercase tracking-widest hidden md:block">
               原点速记法
             </div>
             {onDisplaySettingsChange && onDisplaySettingsReset && (
               <ReaderSettingsMenu
                 settings={displaySettings}
                 onChange={onDisplaySettingsChange}
                 onReset={onDisplaySettingsReset}
               />
             )}
             <button 
               onClick={enterPresentation}
               className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-lg hover:shadow-indigo-500/50"
             >
                <Play className="w-3 h-3 fill-current" />
                演示模式
             </button>
          </div>
        </div>
      </div>

      {/* 2. Hero Section - Fixed at top */}
      <div className="shrink-0 bg-white border-b border-slate-200 shadow-sm z-20">
        <div className="max-w-4xl mx-auto px-4 py-4 md:px-6 md:py-8 flex flex-col items-center text-center">
            {/* Symbol Box - Smaller on mobile */}
            <div className="w-14 h-14 md:w-20 md:h-20 bg-slate-900 rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl border-4 border-amber-500 mb-3 md:mb-6 transform hover:scale-105 transition-transform duration-300">
              <span className="text-3xl md:text-5xl font-serif text-amber-50">{data.char}</span>
            </div>
            
            {/* Tags with Arrow Logic - Tighter on mobile */}
            {hasTags && (
              <div className="flex flex-wrap justify-center items-center gap-1.5 md:gap-3 animate-fade-in-up">
                {data.tags.map((tag, idx) => (
                  <React.Fragment key={idx}>
                    <div className="px-2.5 py-1 md:px-5 md:py-2 bg-amber-50 text-amber-900 text-xs md:text-base font-bold rounded-lg md:rounded-xl border border-amber-200 shadow-sm hover:shadow-md transition-all hover:bg-amber-100 whitespace-nowrap">
                      {tag}
                    </div>
                    {idx < data.tags.length - 1 && (
                      <ArrowRight className="w-3 h-3 md:w-5 md:h-5 text-slate-300" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* 3. Main Content Area - Split Layout with Independent Scrolling */}
      <div className="flex-1 overflow-hidden"> {/* Parent is hidden to contain scroll children */}
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row h-full">
          
          {/* LEFT COLUMN: Origins (Logic) - Independent Scroll */}
          <div className={`flex-1 overflow-y-auto p-3 md:p-6 lg:p-10 space-y-5 md:space-y-8 bg-slate-50 ${hasStory ? 'lg:border-r border-slate-200' : ''} scroll-smooth`}>
            <div className="flex items-center space-x-3 border-b-2 border-slate-200 pb-3 mb-4 md:mb-6">
              <div className="p-1.5 bg-indigo-100 rounded-lg">
                <Brain className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900">象意解析</h3>
            </div>
            
            <div className="grid gap-6 pb-10">
              {data.coreOrigins.map((origin, idx) => (
                <div key={idx} className="bg-white rounded-xl p-3.5 md:p-6 shadow-sm border border-slate-200 relative overflow-hidden group hover:shadow-md transition-shadow">
                  <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-80 group-hover:opacity-100"></div>
                  <h4 className="text-sm md:text-lg font-bold text-slate-900 mb-2.5 pl-2">{origin.title}</h4>
                  {origin.logic && (
                    <p
                      className="text-slate-600 italic mb-3.5 md:mb-5 bg-slate-50 p-3 md:p-4 rounded-lg border border-slate-100"
                      style={bodyTextCompactStyle}
                    >
                      {origin.logic}
                    </p>
                  )}
                  
                  <div className="pt-1">
                    {origin.subItems.map((sub, sIdx) => (
                      <div
                        key={sIdx}
                        className="flex items-baseline gap-2"
                        style={{ marginBottom: sIdx === origin.subItems.length - 1 ? 0 : paragraphGap }}
                      >
                        <span
                          className="shrink-0 w-[4.8rem] sm:w-[5.6rem] font-semibold text-slate-700 whitespace-nowrap"
                          style={{ fontSize: `${labelFontSize}px`, lineHeight: displaySettings.lineHeight }}
                        >
                          {sub.label}
                        </span>
                        <div className="text-slate-700 flex-1" style={bodyTextStyle}>
                          {renderContent(sub.content)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Only: Story Section (at bottom of scroll, only if story exists) */}
            {hasStory && (
              <div className="lg:hidden pt-8 border-t border-slate-200 mt-8 pb-10">
                 <div className="flex items-center space-x-2 mb-4">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                    <h3 className="text-lg font-bold text-slate-900">场景故事速记</h3>
                 </div>
                 <div className="bg-amber-50 p-5 rounded-xl border border-amber-100">
                    <p className="text-slate-800 font-serif mb-4" style={bodyTextStyle}>“{data.story}”</p>
                    {data.imageUrl && <img src={data.imageUrl} className="w-full rounded-lg shadow-sm" alt="场景故事" />}
                 </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Story & Image - Independent Scroll on Desktop (only if story exists) */}
          {hasStory && (
            <div className="hidden lg:block lg:w-[450px] xl:w-[500px] bg-white border-l border-slate-100 overflow-y-auto h-full">
               <div className="p-8 flex flex-col min-h-full">
                  <div className="flex items-center space-x-3 border-b-2 border-amber-100 pb-3 mb-6">
                    <div className="p-1.5 bg-amber-100 rounded-lg">
                      <BookOpen className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">场景故事速记</h3>
                  </div>

                  <div className="flex-1 flex flex-col gap-6">
                     {/* Story Text Card */}
                     <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl shadow-sm border border-amber-100">
                        <p className="text-slate-800 font-serif" style={bodyTextStyle}>
                         “{data.story}”
                        </p>
                     </div>

                     {/* Image Container */}
                     <div className="relative rounded-2xl overflow-hidden shadow-lg ring-1 ring-slate-900/5 bg-slate-100 group">
                        {data.imageUrl ? (
                           <div className="aspect-[4/3] w-full">
                             <img 
                               src={data.imageUrl} 
                               alt={`${data.char} 场景故事`} 
                               className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                             />
                           </div>
                         ) : (
                           <div className="aspect-[4/3] flex flex-col items-center justify-center text-amber-800/40">
                              <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                              <span className="text-sm font-medium">脑海成像区</span>
                           </div>
                         )}
                         {/* Overlay Gradient */}
                         <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
                     </div>

                     <div className="mt-auto text-center py-4">
                        <p className="text-xs text-slate-400">左脑逻辑梳理 · 右脑图像定格</p>
                     </div>
                  </div>
               </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default DetailView;
